import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, Shield, Bell, Lock, CheckCircle, BarChart3, FileText, Link2, CreditCard, Eye, Monitor, Smartphone, Tablet, Star, Zap, UserCheck, Settings, HeartHandshake, Gift, Home, Calendar, BookOpen, Scale, RefreshCw, Ban, MessageCircle, Receipt, EyeOff, Clock } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const PageCoParentalite = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Co-Parentalité | Gestion Collaborative"; }, []);

  return (
    <div className="home-container">
      <Header currentPage="coparentalite" lang="fr" />

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <HeartHandshake size={16} />
            <span>Module Co-Parentalité</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-parents)' }}>Co-Parentalité</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            <strong style={{ color: 'var(--actor-parents)' }}>Module de gestion collaborative pour familles modernes</strong>
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">2+</div>
              <div className="stat-label">Parents connectés</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">50/50</div>
              <div className="stat-label">Split automatique</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Sécurisé</div>
            </div>
          </div>
        </div>
      </section>

      {/* Module de Gestion Collaborative */}
      <section id="gestion-collaborative" className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Module de Gestion Collaborative</h2>
            <p className="section-subtitle">
              Accès partagé et permissions configurables pour une co-parentalité harmonieuse
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Accès Multi-Parents</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Invitation via lien unique</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Validation double approbation</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Synchronisation temps réel</li>
              </ul>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Settings size={32} /></div>
              <h3>Permissions Configurables</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Qui peut commander</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Qui peut consulter</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Qui peut payer</li>
              </ul>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Sécurité Renforcée</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Paiements chiffrés séparés</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Journal d'activité complet</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Traçabilité IP et horodatage</li>
              </ul>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Notifications Intelligentes</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Alertes temps réel</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Approbations interactives</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Historique des actions</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Partage des Paiements */}
      <section className="section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Partage des Paiements</h2>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Split Automatique</h3>
              <p>Options prédéfinies : 50/50, 60/40, 70/30 ou pourcentage personnalisé</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Validation Double</h3>
              <p>Les deux parents doivent approuver la répartition avant activation</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Lock size={32} /></div>
              <h3>Paiements Chiffrés Séparés</h3>
              <p>Chaque parent entre son moyen de paiement. Aucun ne voit les infos de l'autre</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Factures Individuelles</h3>
              <p>Reçus séparés automatiques. Rapport PDF annuel exportable</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Link2 size={32} /></div>
              <h3>Lien de Paiement Unique</h3>
              <p>Envoyez un lien de paiement à des tiers (grands-parents, famille, amis) pour contribution ponctuelle sans inscription</p>
            </div>
          </div>
        </div>
      </section>

      {/* Lien de Paiement Externe */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Lien de Paiement Externe — <span style={{color: 'var(--actor-parents)'}}>Fonctionnalité Unique</span></h2>
            <p className="section-subtitle">
              Parent gère la commande complète, tiers paie via interface dédiée
            </p>
          </div>

          <h3 style={{color: 'white', marginBottom: '1.5rem', fontSize: '1.2rem'}}>Comment ça Fonctionne</h3>

          <div className="benefits-grid">
            <div className="benefit-card" style={{borderTop: '3px solid #00D4FF'}}>
              <div style={{background: '#00D4FF', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>1</div>
              <h3>Parent crée la commande complète</h3>
              <p>Configure tous les repas, menus et préférences pour son/ses enfant(s) via son portail</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #84CC16'}}>
              <div style={{background: '#84CC16', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>2</div>
              <h3>Génération lien personnalisé</h3>
              <p>Système génère un lien de paiement unique et sécurisé pour cette commande spécifique</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #FF8C00'}}>
              <div style={{background: '#FF8C00', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>3</div>
              <h3>Envoi au payeur tiers</h3>
              <p>Parent envoie le lien (email, SMS, WhatsApp) à la personne qui va effectuer le paiement</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #C084FC'}}>
              <div style={{background: '#C084FC', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>4</div>
              <h3>Interface frontend dédiée</h3>
              <p>Tiers accède à une page web simple et sécurisée affichant la commande et le montant à payer</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #E74C3C'}}>
              <div style={{background: '#E74C3C', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>5</div>
              <h3>Paiement sans inscription</h3>
              <p>Tiers effectue le paiement directement via plateforme (carte bancaire) sans créer de compte</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #06B6D4'}}>
              <div style={{background: '#06B6D4', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>6</div>
              <h3>Confirmation instantanée</h3>
              <p>Parent et tiers reçoivent confirmation immédiate avec reçu de paiement</p>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="section roi-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem'}}>
            <div className="roi-card" style={{padding: '2rem'}}>
              <h3 style={{marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}><Star size={22} style={{color: '#FF8C00'}} /> Avantages pour Parent</h3>
              <ul style={{listStyle: 'none', padding: 0, lineHeight: '2.2', color: 'var(--color-text-secondary)'}}>
                <li><UserCheck size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Contrôle total sur commande et choix alimentaires</li>
                <li><BarChart3 size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Flexibilité financière — déléguer paiement facilement</li>
                <li><Eye size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Transparence — tiers voit exactement ce qu'il paie</li>
                <li><FileText size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Historique complet de qui a payé quoi et quand</li>
              </ul>
            </div>
            <div className="roi-card" style={{padding: '2rem'}}>
              <h3 style={{marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}><Users size={22} style={{color: '#E74C3C'}} /> Avantages pour Tiers</h3>
              <ul style={{listStyle: 'none', padding: 0, lineHeight: '2.2', color: 'var(--color-text-secondary)'}}>
                <li><Zap size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Ultra simple — 1 clic, paiement, terminé</li>
                <li><CreditCard size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Aucune inscription requise pour payer</li>
                <li><Shield size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Sécurité — paiement via plateforme certifiée</li>
                <li><FileText size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Reçu instantané par email pour preuve</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Extension Parent Portal */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Extension Parent Portal</h2>
            <p className="section-subtitle">Interface Web + Mobile dédiée</p>
          </div>

          <div className="roi-grid">
            <div className="roi-card">
              <div className="roi-metric"><Monitor size={28} style={{color: '#00D4FF'}} /><Tablet size={22} style={{color: '#00D4FF'}} /><Smartphone size={18} style={{color: '#00D4FF'}} /></div>
              <ul style={{listStyle: 'none', padding: 0, lineHeight: '2.2', color: 'var(--color-text-secondary)', marginTop: '1rem', textAlign: 'left'}}>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Interface Web + Mobile dédiée</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Dashboard partagé 2 parents</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Permissions granulaires configurables</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Split paiement automatique</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Page de paiement tiers (sans compte)</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Historique & audit complet</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Cas d'Usage Typiques */}
      <section className="section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Cas d'Usage Typiques</h2>
          </div>
          <div className="roi-grid">
            <div className="roi-card" style={{textAlign: 'center', padding: '2rem'}}>
              <HeartHandshake size={36} style={{color: '#00D4FF', marginBottom: '1rem'}} />
              <h3>Grands-Parents</h3>
              <p style={{color: 'var(--color-text-secondary)', marginTop: '0.5rem'}}>Contribuent aux repas de leurs petits-enfants</p>
            </div>
            <div className="roi-card" style={{textAlign: 'center', padding: '2rem'}}>
              <Gift size={36} style={{color: '#FF8C00', marginBottom: '1rem'}} />
              <h3>Cadeau Anniversaire</h3>
              <p style={{color: 'var(--color-text-secondary)', marginTop: '0.5rem'}}>Offrir des repas scolaires comme cadeau utile</p>
            </div>
            <div className="roi-card" style={{textAlign: 'center', padding: '2rem'}}>
              <Users size={36} style={{color: '#C084FC', marginBottom: '1rem'}} />
              <h3>Famille Élargie</h3>
              <p style={{color: 'var(--color-text-secondary)', marginTop: '0.5rem'}}>Oncles, tantes, parrains qui veulent aider</p>
            </div>
          </div>
        </div>
      </section>

      {/* Scénarios d'Usage */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Scénarios d'Usage</h2>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Garde Partagée</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Semaine A/B</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Week-ends alternés</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Vacances scolaires</li>
              </ul>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><HeartHandshake size={32} /></div>
              <h3>Grands-Parents Impliqués</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Accès lecture seule</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Suivi des enfants</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Participation financière optionnelle</li>
              </ul>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Parents Séparés</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Transparence totale</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Évite conflits financiers</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Communication fluide</li>
              </ul>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Scale size={32} /></div>
              <h3>Tuteurs Multiples Légaux</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Droits configurables</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Gestion multi-acteurs</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Conformité légale</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages Clés */}
      <section className="section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Avantages Clés</h2>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Transparence Totale</h3>
              <p>Toutes les actions visibles. Historique partagé complet.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Ban size={32} /></div>
              <h3>Évite les Doublons</h3>
              <p>Commandes synchronisées. Alertes anti-doublon.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><MessageCircle size={32} /></div>
              <h3>Communication Fluide</h3>
              <p>Notifications contextuelles. Validation 1 clic.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Receipt size={32} /></div>
              <h3>Gestion Financière Claire</h3>
              <p>Split automatique. Factures séparées. Rapports détaillés.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><EyeOff size={32} /></div>
              <h3>Confidentialité Garantie</h3>
              <p>Données bancaires chiffrées. Permissions granulaires.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><RefreshCw size={32} /></div>
              <h3>Synchro Temps Réel</h3>
              <p>Mise à jour instantanée. 0 conflit.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comment Ça Marche */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Comment Ça Marche</h2>
          </div>
          <div className="benefits-grid" style={{maxWidth: '900px', margin: '0 auto', gridTemplateColumns: 'repeat(2, 1fr)'}}>
            <div className="benefit-card" style={{borderTop: '3px solid #00D4FF'}}>
              <div style={{background: '#00D4FF', width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem', fontSize: '1.1rem'}}>1</div>
              <h3>Configuration</h3>
              <p>Parent principal configure et invite l'autre parent</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #84CC16'}}>
              <div style={{background: '#84CC16', width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem', fontSize: '1.1rem'}}>2</div>
              <h3>Acceptation</h3>
              <p>Parent invité accepte et crée son compte</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #FF8C00'}}>
              <div style={{background: '#FF8C00', width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem', fontSize: '1.1rem'}}>3</div>
              <h3>Validation</h3>
              <p>Les deux parents valident le partage paiement</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #C084FC'}}>
              <div style={{background: '#C084FC', width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem', fontSize: '1.1rem'}}>4</div>
              <h3>Automatisation</h3>
              <p>Les transactions futures sont automatiquement partagées</p>
            </div>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>
              <div className="footer-column">
                <h4>Plateforme</h4>
                <a onClick={() => navigate('/platform')}>Architecture</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-legal">
              <span>&copy; 2025 EcoLunch. Tous droits réservés.</span>
              <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontWeight: 600, textDecoration: 'none'}}>ecotechsysteme.ca</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageCoParentalite;
